<template>
    <!-- Lives in the layout, so it is the same button on every screen. A plain
         `button` rather than `v-btn`: the collapsing pill needs its own width,
         padding and shadow, and overriding all three on `v-btn` costs more than
         the component gives back. -->
    <button
        v-if="isVisible"
        type="button"
        class="assistant-launcher"
        :class="{ ['assistant-launcher--open']: isHinting }"
        :title="$t('pages.assistant.launcher.hint')"
        data-test-id="assistant--entry-btn"
        @click="openPanel"
    >
        <span class="assistant-launcher__halo" aria-hidden="true"></span>
        <!-- Sparkles rather than a microphone: dictation is how the assistant
             is fed, not what it is. The microphone stays on the composer, where
             it is the actual control. -->
        <span class="assistant-launcher__icon">
            <v-icon dark size="22">mdi-creation</v-icon>
        </span>
        <span class="assistant-launcher__label">
            {{ $t('pages.assistant.launcher.label') }}
        </span>
    </button>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
    actionTypes as actionTypesModals,
    getterTypes as getterTypesModals,
} from '@/store/modules/modals'
import { gettersTypes as gettersTypesAuth } from '@/store/modules/auth'
import { assistantGetterTypes } from '@/store/modules/assistant/types'
import popUpTypes from '@/types/pop-up-types'

const { assistantPanel } = popUpTypes

// Two page loads is enough to be noticed and few enough not to nag: after that
// the pill only opens on hover.
const HINT_STORAGE_KEY = 'assistant-launcher-hints'
const HINT_LIMIT = 2
const HINT_DURATION = 4000

export default {
    name: 'AssistantLauncher',
    data() {
        return {
            isHinting: false,
            hintTimer: null,
        }
    },
    computed: {
        ...mapGetters('auth', { getAllowed: gettersTypesAuth.getAllowed }),
        ...mapGetters('modals', { openModals: getterTypesModals.getModals }),
        ...mapGetters('assistant', {
            isSwitchedOff: assistantGetterTypes.GET_IS_SWITCHED_OFF,
        }),
        isVisible: function () {
            // The getter folds MANAGER_PLUS into MANAGER, so these two names
            // are exactly the three role ids the backend allows.
            if (!this.getAllowed(['admin', 'manager'])) return false

            // Switched off by an administrator — only a reload brings it back.
            if (this.isSwitchedOff) return false

            // Every pop-up in this app is a Vuetify dialog, which draws its
            // scrim above the button anyway — and the assistant panel itself
            // sits below that dialog layer, so opening it from over a dialog
            // would put it out of reach. Stepping aside is the honest version
            // of what the stacking order already does.
            return this.openModals.length === 0
        },
    },
    watch: {
        isVisible: {
            immediate: true,
            handler: function (isVisible) {
                if (isVisible) this.hintOnce()
            },
        },
    },
    // The linter asks for `beforeUnmount`, which does not exist on this Vue 2.6
    // runtime and would never fire, leaving the hint timer behind.
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy() {
        clearTimeout(this.hintTimer)
    },
    methods: {
        ...mapActions('modals', { onOpen: actionTypesModals.openPopUp }),
        /**
         * Opens the pill the first couple of times a manager loads the app, so
         * the icon alone never has to carry the meaning. Runs once per load —
         * closing a dialog brings the button back, not the hint.
         */
        hintOnce: function () {
            if (this.hintTimer) return

            const shown = this.readHintCount()
            if (shown >= HINT_LIMIT) return

            this.writeHintCount(shown + 1)
            this.isHinting = true
            this.hintTimer = setTimeout(() => {
                this.isHinting = false
            }, HINT_DURATION)
        },
        // Storage is unavailable in a few browser modes, and a hint is not
        // worth an exception: no counter simply means no hint.
        readHintCount: function () {
            try {
                return Number(localStorage.getItem(HINT_STORAGE_KEY)) || 0
            } catch (error) {
                return HINT_LIMIT
            }
        },
        writeHintCount: function (count) {
            try {
                localStorage.setItem(HINT_STORAGE_KEY, String(count))
            } catch (error) {
                // Nothing to do — the hint just repeats next load.
            }
        },
        openPanel: function () {
            this.onOpen({ name: assistantPanel })
        },
    },
}
</script>

<style scoped lang="scss">
.assistant-launcher {
    position: fixed;
    bottom: 16px;
    right: 16px;
    // Above the header (100) and the side bar (101), below Vuetify's dialog
    // layer (~200) — the button is part of the page, not of a dialog.
    z-index: 120;
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 16px;
    border: none;
    border-radius: 28px;
    // The assistant purple, lit at one end and cooled by a trace of the system
    // blue at the other — enough of a shift to read as one live thing rather
    // than another toolbar button, not enough to stop being purple.
    background: linear-gradient(
        135deg,
        #a78ee5 0%,
        #967adc 48%,
        #7d6ad6 78%,
        #6d7fd2 100%
    );
    color: $color-primary-light;
    box-shadow: 0 6px 18px rgba(70, 50, 130, 0.35);
    cursor: pointer;
    animation: assistant-launcher-in 420ms cubic-bezier(0.34, 1.56, 0.64, 1)
        600ms both;
    transition: box-shadow 200ms ease, transform 200ms ease;

    // `dir` mirrors the flow inside the button; the corner it sits in is
    // physical and has to be moved by hand.
    @include for-hebrew {
        right: auto;
        left: 16px;
    }

    @include tablet-up {
        bottom: 24px;
        right: 24px;

        @include for-hebrew {
            right: auto;
            left: 24px;
        }
    }

    @media print {
        display: none;
    }

    &:hover {
        box-shadow: 0 10px 26px rgba(70, 50, 130, 0.45);
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0) scale(0.97);
    }

    &:focus-visible {
        outline: 2px solid #6a4fb6;
        outline-offset: 3px;
    }

    // A fixed box keeps the collapsed pill an exact circle whatever the
    // glyph measures.
    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        flex-shrink: 0;
    }

    // Collapsed by default and opened by hover, focus or the first-loads hint —
    // a width transition rather than a mount, so the icon never jumps.
    &__label {
        max-width: 0;
        overflow: hidden;
        white-space: nowrap;
        opacity: 0;
        font-family: $font-varela-round;
        font-size: 15px;
        line-height: 1;
        transition: max-width 260ms cubic-bezier(0.4, 0, 0.2, 1),
            opacity 160ms ease, margin 260ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover &__label,
    &:focus-visible &__label,
    &--open &__label {
        max-width: 220px;
        opacity: 1;
        margin-left: 10px;

        @include for-hebrew {
            margin-left: 0;
            margin-right: 10px;
        }
    }

    // The one moving part: a ring of the button's own colour pinging outwards
    // every few seconds, so a still screen still points at it. Hollow on
    // purpose — a filled shape would wash over the gradient it grows out of.
    &__halo {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        border: 2px solid #967adc;
        border-radius: inherit;
        opacity: 0;
        pointer-events: none;
        animation: assistant-launcher-halo 6s ease-out 1600ms infinite;
    }

    // Noticed — the pulse has done its job and stops pulling. Dropped rather
    // than paused: a paused animation keeps painting its last frame.
    &:hover &__halo,
    &:focus-visible &__halo {
        animation: none;
    }
}

@keyframes assistant-launcher-in {
    from {
        opacity: 0;
        transform: scale(0.6);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes assistant-launcher-halo {
    0% {
        transform: scale(1);
        opacity: 0.55;
    }

    22% {
        transform: scale(1.3);
        opacity: 0;
    }

    100% {
        transform: scale(1.3);
        opacity: 0;
    }
}

// A button that pulses on its own is exactly what this setting asks to stop.
@media (prefers-reduced-motion: reduce) {
    .assistant-launcher,
    .assistant-launcher__halo,
    .assistant-launcher__label {
        animation: none;
        transition: none;
    }

    .assistant-launcher:hover {
        transform: none;
    }
}
</style>
